import { useEffect, useRef, useState } from 'react';

import { getUserData } from '../../../constants/global';
import type { ICitizensSitrepContext, ICitizensSitrepItem, ICoreReportItem } from '../../../interface/child-agent';
import {
	canQueryCitizensSitrep,
	filterReportsByUser,
	getCitizensSituationReports,
	getCoreReportsForAgent,
} from '../../../lib/child-sitrep-api';
import Lightbox from '../child-agent/Lightbox';
import TabMessage from '../child-agent/tabs/TabMessage';
import { relativeTime } from '../child-agent/utils';
import { MegaphoneIcon, WarnGlyph } from './components';

type ViewState = 'loading' | 'ready' | 'error';

interface IUnifiedReport {
	id: string;
	source: 'incident' | 'citizen';
	title: string;
	body?: string;
	createdAt?: string;
	media: string[];
	resolved?: boolean;
	lat?: string | number;
	lng?: string | number;
}

const toUnifiedIncident = (r: ICoreReportItem): IUnifiedReport => ({
	id: `incident-${r.id}`,
	source: 'incident',
	title: r.report?.title || 'Incident report',
	body: r.report?.body,
	createdAt: r.createdAt,
	media: [],
	resolved: r.isResolved,
});

const toUnifiedCitizen = (r: ICitizensSitrepItem): IUnifiedReport => ({
	id: `citizen-${r.id}`,
	source: 'citizen',
	title: r.situation || 'Situation report',
	createdAt: r.createdAt,
	media: (r.mediaUrl ?? []).filter(Boolean),
	lat: r.latitude,
	lng: r.longitude,
});

interface ISituationTabProps {
	sitrepCtx: Partial<ICitizensSitrepContext>;
	onCreate: () => void;
}

const SituationTab = ({ sitrepCtx, onCreate }: ISituationTabProps) => {
	const [state, setState] = useState<ViewState>('loading');
	const [reports, setReports] = useState<IUnifiedReport[]>([]);
	const [citizenDegraded, setCitizenDegraded] = useState(false);
	const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
	const loadedRef = useRef(false);

	const userId = getUserData().user?.UserId;

	const load = () => {
		setState('loading');
		const canCitizen = canQueryCitizensSitrep(sitrepCtx);
		setCitizenDegraded(!canCitizen);

		const incidentP = getCoreReportsForAgent();
		const citizenP = canCitizen ? getCitizensSituationReports(sitrepCtx) : Promise.resolve(null);

		Promise.all([incidentP, citizenP]).then(([incidentOut, citizenOut]) => {
			const incidentReachable = incidentOut.reachable;
			const incidents =
				incidentOut.reachable && incidentOut.httpStatus >= 200 && incidentOut.httpStatus < 300
					? filterReportsByUser(incidentOut.items, userId).map(toUnifiedIncident)
					: [];

			const citizens =
				citizenOut && citizenOut.reachable && citizenOut.httpStatus >= 200 && citizenOut.httpStatus < 300
					? citizenOut.items.map(toUnifiedCitizen)
					: [];

			if (!incidentReachable && citizens.length === 0) {
				setState('error');
				return;
			}

			const merged = [...incidents, ...citizens].sort(
				(a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
			);
			setReports(merged);
			setState('ready');
		});
	};

	useEffect(() => {
		if (loadedRef.current) return;
		loadedRef.current = true;
		load();
	}, []);

	const CreateCta = (
		<button
			type='button'
			className='vsr-sitrep-cta'
			onClick={onCreate}
		>
			<MegaphoneIcon />
			Create Situation Report
		</button>
	);

	if (state === 'loading') {
		return (
			<div className='ca-sitrep f-column-15'>
				<div className='ca-sitrep__sk-card skeleton' />
				<div className='ca-sitrep__sk-card skeleton' />
			</div>
		);
	}

	if (state === 'error') {
		return (
			<div className='f-column-20'>
				{CreateCta}
				<TabMessage
					icon={<WarnGlyph />}
					tone='danger'
					title="We couldn't load situation reports."
					hint='The reports service is temporarily unavailable.'
					action={{ label: 'Try again', onClick: load }}
				/>
			</div>
		);
	}

	return (
		<div className='ca-sitrep f-column-15'>
			{CreateCta}

			{!reports.length ? (
				<TabMessage
					icon={<MegaphoneIcon />}
					tone='neutral'
					title='No situation reports yet'
					hint='Reports you file for this location will appear here.'
				/>
			) : (
				reports.map((r) => (
					<article
						className='ca-sitrep__card'
						key={r.id}
					>
						<div className='ca-sitrep__card-head'>
							<span className={`ca-sitrep__source ca-sitrep__source--${r.source}`}>
								{r.source === 'incident' ? 'Incident' : 'Situation'}
							</span>
							{r.createdAt ? <span className='ca-sitrep__time'>{relativeTime(r.createdAt)}</span> : null}
							{r.resolved ? <span className='ca-sitrep__resolved'>Resolved</span> : null}
						</div>
						<p className='ca-sitrep__title'>{r.title}</p>
						{r.body ? <p className='ca-sitrep__body'>{r.body}</p> : null}
						{r.media.length ? (
							<div className='ca-sitrep__media'>
								{r.media.map((url, i) => (
									<button
										type='button'
										className='ca-sitrep__thumb'
										key={url}
										onClick={() => setLightbox({ images: r.media, index: i })}
										aria-label={`Open report photo ${i + 1}`}
									>
										<img
											src={url}
											alt={`Report ${i + 1}`}
											loading='lazy'
										/>
									</button>
								))}
							</div>
						) : null}
						{r.lat && r.lng ? (
							<a
								className='ca-sitrep__map-link'
								href={`https://www.openstreetmap.org/?mlat=${r.lat}&mlon=${r.lng}#map=16/${r.lat}/${r.lng}`}
								target='_blank'
								rel='noopener noreferrer'
							>
								View on map
							</a>
						) : null}
					</article>
				))
			)}

			{citizenDegraded ? (
				<p className='ca-sitrep__footnote'>Citizen-submitted reports for this unit aren't available in this view yet.</p>
			) : null}

			{lightbox ? (
				<Lightbox
					images={lightbox.images}
					startIndex={lightbox.index}
					label='Report'
					onClose={() => setLightbox(null)}
				/>
			) : null}
		</div>
	);
};

export default SituationTab;
